/* ============================================================
   customerOrderView — TASK-252: pure view model for the customer
   order detail. Derives, from an OrderDetail only:
   - `banner`: the single "what now" card — action-oriented when
     the ball is in the customer's court, an explicit waiting /
     terminal state otherwise (no dead ends);
   - `action`: which existing workflow block to open (confirm the
     result, or dispute a shipped order) — workflows unchanged;
   - `cues`: scannable state chips (photo counts by kind, customer
     confirmation, open negotiation, tracking) mirroring the
     provider queue cards (TASK-253);
   - `photos`: progress vs result groups, chronological, with the
     result-submitted timestamp the timeline stage relies on.
   No fake data; every cue comes from real API fields. Light theme,
   Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type { CounterOffer, OrderResultPhoto, WebDeliveryMethod, WebOrderStatus } from '../types/product.ts';
import { DELIVERY_METHOD_LABELS, normalizeCounterOfferStatus, normalizeDeliveryMethod, normalizeOrderStatus } from '../types/product.ts';
import { buildShippedTrackingView, isPickupDeliveryMethod } from './shippedTrackingView.ts';

export type CustomerCueTone = 'neutral' | 'accent' | 'success' | 'danger';

/** Which existing workflow block the banner should open (null = none). */
export type CustomerActionKind = 'confirmResult' | 'dispute';

export interface CustomerBanner {
  tone: 'accent' | 'neutral' | 'success' | 'danger';
  title: string;
  /** Secondary line; null keeps the banner to one scannable sentence. */
  text: string | null;
}

export interface CustomerPhotoView {
  photoId: string;
  url: string;
  caption: string | null;
  createdAtUtc: string;
}

export interface CustomerOrderView {
  banner: CustomerBanner;
  action: { kind: CustomerActionKind } | null;
  cues: Array<{ label: string; tone: CustomerCueTone }>;
  photos: { progress: CustomerPhotoView[]; result: CustomerPhotoView[] };
  /** Earliest result-proof (kind 0) timestamp — feeds the timeline result stage. */
  resultSubmittedAtUtc: string | null;
  /** TASK-240 (audit P1 substance 6bdd9dc3): the cancel block is offered only
   *  while work has not started (Pending/Accepted) — OrderService guard parity,
   *  so the web never advertises a POST the server will reject. */
  canCancel: boolean;
  /** G8-D1: delivery method/cost line for detail meta + totals rows. Never null — legacy rows degrade to «стоимость уточняется». */
  delivery: CustomerDeliveryView;
}

/** Narrow structural input — decoupled from OrderDetail so it stays pure. */
export interface CustomerOrderViewInput {
  status: WebOrderStatus | number;
  createdAtUtc: string;
  customerConfirmedAtUtc?: string | null;
  completedAtUtc?: string | null;
  disputedAtUtc?: string | null;
  /** TASK-667a55be: dispute context — own reason echo + support resolution. */
  disputeReason?: string | null;
  disputeResolutionNote?: string | null;
  disputeResolvedAtUtc?: string | null;
  trackingNumber?: string | null;
  estimatedDeliveryUtc?: string | null;
  /** audit4-A2: when silence auto-confirms the result (server-computed P2 basis + ConfirmTimeoutHours). Null outside Printed-unconfirmed. */
  confirmDeadlineUtc?: string | null;
  resultPhotos: OrderResultPhoto[];
  counterOffers?: CounterOffer[] | null;
  /** G8-D1: delivery snapshot from the order (numeric enum tolerated; absent = legacy Courier). */
  deliveryMethod?: WebDeliveryMethod | number | null;
  deliveryCostRub?: number | null;
  /** 31c9ace7: carrier behind the checkout delivery tariff — feeds the
      shipped-banner tracking view (preferred over shape detection). */
  deliveryCarrier?: string | null;
  /** G8-D1: order kind — PerformerCart delivery was charged into the total (exact, not estimate). */
  kind?: 'CustomQuote' | 'SellerProducts' | 'PerformerCart' | number | null;
}

/** G8-D1: delivery line for meta rows and the totals block. */
export interface CustomerDeliveryView {
  method: WebDeliveryMethod;
  methodLabel: string;
  /** Snapshot cost; null = unknown (legacy courier rows without a cost). */
  costRub: number | null;
  /** True when the cost is part of the order total (cart quote-print orders). */
  includedInTotal: boolean;
  /** One-line summary: «Самовывоз — бесплатно» / «Курьер — 350 ₽» / «Курьер — ≈350 ₽» / «Курьер — стоимость уточняется». */
  text: string;
}

/** G8-D1: pure delivery-line derivation, shared with providerOrderView. */
export function buildDeliveryView(input: {
  deliveryMethod?: WebDeliveryMethod | number | null;
  deliveryCostRub?: number | null;
  kind?: 'CustomQuote' | 'SellerProducts' | 'PerformerCart' | number | null;
}): CustomerDeliveryView {
  const method = normalizeDeliveryMethod(input.deliveryMethod ?? null);
  const methodLabel = DELIVERY_METHOD_LABELS[method];
  const rawCost = Number(input.deliveryCostRub);
  const costRub = Number.isFinite(rawCost) && rawCost > 0 ? rawCost : null;
  const includedInTotal = input.kind === 'PerformerCart' || input.kind === 2;

  let text: string;
  if (method === 'Pickup') {
    text = `${methodLabel} — бесплатно`;
  } else if (costRub != null) {
    text = includedInTotal
      ? `${methodLabel} — ${costRub.toLocaleString('ru-RU')} ₽`
      : `${methodLabel} — ≈${costRub.toLocaleString('ru-RU')} ₽`;
  } else {
    text = `${methodLabel} — стоимость уточняется`;
  }

  return { method, methodLabel, costRub, includedInTotal, text };
}

/** TASK-256: calendar-day comparison — an ETA counts as passed only after its day ended. */
export function isPastCalendarDay(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() < now.getFullYear()
    || (d.getFullYear() === now.getFullYear() && (d.getMonth() < now.getMonth()
      || (d.getMonth() === now.getMonth() && d.getDate() < now.getDate())))
  );
}

/** TASK-258: human delivery-day label — «сегодня» on the promised day,
 * localized date otherwise; empty string for unparsable input. */
export function formatEtaDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  return sameDay ? 'сегодня' : d.toLocaleDateString('ru-RU');
}

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function openOffer(offers: CounterOffer[] | null | undefined): CounterOffer | null {
  return (offers ?? []).find((o) => normalizeCounterOfferStatus(o.status) === 'Pending') ?? null;
}

/** Pure customer detail view model — trivially unit-testable, no React dependency. */
export function buildCustomerOrderView(order: CustomerOrderViewInput): CustomerOrderView {
  const status = normalizeOrderStatus(order.status);
  const confirmed = order.customerConfirmedAtUtc != null;
  const offer = openOffer(order.counterOffers);
  // TASK-240: status-aware cancel affordance, kept in the pure view so the
  // gating is unit-testable in node (mirrors the server-side explicit guard
  // in OrderService.CancelOrderAsync, not the raw state machine).
  const canCancel = status === 'Pending' || status === 'Accepted';

  // TASK-250: kind 0 = final result proof, kind 1 = in-progress update.
  // ISO strings sort chronologically, so no Date parsing is needed.
  const byTime = (a: OrderResultPhoto, b: OrderResultPhoto) => a.createdAtUtc.localeCompare(b.createdAtUtc);
  const progress = order.resultPhotos
    .filter((p) => (p.kind ?? 0) === 1)
    .sort(byTime)
    .map((p) => ({ photoId: p.photoId, url: p.url, caption: p.caption, createdAtUtc: p.createdAtUtc }));
  const result = order.resultPhotos
    .filter((p) => (p.kind ?? 0) === 0)
    .sort(byTime)
    .map((p) => ({ photoId: p.photoId, url: p.url, caption: p.caption, createdAtUtc: p.createdAtUtc }));
  // Only final result proof (kind 0) marks submission — progress photos are
  // usually shared earlier and must not fake the timeline (TASK-250/251).
  const resultSubmittedAtUtc = result.length > 0 ? result[0].createdAtUtc : null;

  // ── Cues: scannable chips mirroring the provider card (TASK-253) ──
  const cues: CustomerOrderView['cues'] = [];
  if (progress.length > 0) {
    cues.push({ label: `📷 ${progress.length} фото процесса`, tone: 'neutral' });
  }
  if (result.length > 0) {
    cues.push({ label: `🖼 ${result.length} фото результата`, tone: 'neutral' });
  }
  if (status === 'Printed' && confirmed) {
    cues.push({ label: '✓ Результат подтверждён', tone: 'success' });
  }
  if (offer) {
    cues.push(
      offer.isFromProvider
        ? { label: `Предложение условий: ${offer.proposedPriceRub.toLocaleString('ru-RU')} ₽`, tone: 'accent' }
        : { label: 'Ждём ответа на ваше предложение', tone: 'neutral' },
    );
  }
  if (order.trackingNumber) {
    cues.push({ label: `📦 ${order.trackingNumber}`, tone: 'neutral' });
  }
  // TASK-256: list/detail parity — the frozen state gets the same danger
  // chip the row shows (TASK-255), not only the banner treatment.
  if (status === 'Disputed') {
    cues.push({ label: 'Открыт спор', tone: 'danger' });
  }

  // ── Banner + action: one clear "what now" per state ──
  let banner: CustomerBanner;
  let action: CustomerOrderView['action'] = null;

  switch (status) {
    case 'Pending':
      // TASK-256: the ball can sit with the provider in two ways — they
      // must answer the customer's counter, or take/re-offer the order.
      banner = offer
        ? offer.isFromProvider
          ? {
              tone: 'accent',
              title: 'Исполнитель предложил свои условия',
              text: 'Ответьте в карточке предложения — принять, отклонить или встречное.',
            }
          : {
              tone: 'neutral',
              title: 'Ждём ответа исполнителя',
              text: 'Вы предложили свои условия — исполнитель ответит в карточке предложения.',
            }
        : {
            tone: 'neutral',
            title: 'Заказ ожидает исполнителя',
            text: 'Исполнитель примет заказ или предложит свои условия.',
          };
      break;
    case 'Accepted':
      banner = {
        tone: 'neutral',
        title: 'Исполнитель принял заказ',
        text: 'Печать начнётся в согласованное время.',
      };
      break;
    case 'Printing':
      banner = {
        tone: 'neutral',
        title: 'Заказ печатается',
        text: progress.length > 0 ? 'Следите за фото процесса — результат будет опубликован отдельно.' : 'Исполнитель может делиться фото процесса по мере печати.',
      };
      break;
    case 'Printed':
      if (confirmed) {
        // TASK-248: the waiting copy follows the G8-D1 delivery method —
        // pickup orders are prepared for collection, not dispatched, so
        // «отправит» would promise a tracking stage that never comes.
        const waitText = isPickupDeliveryMethod(order.deliveryMethod)
          ? 'Ждём, когда заказ будет готов к выдаче.'
          : 'Ждём, когда исполнитель отправит заказ.';
        banner = {
          tone: 'success',
          title: 'Результат подтверждён',
          text: `Подтверждено ${formatTs(order.customerConfirmedAtUtc!)}. ${waitText}`,
        };
      } else {
        // audit4-A2 (5fecaf32): disclose the P2 confirm window where it binds —
        // one cue line with the server-computed deadline; absent (job off /
        // legacy payload) keeps the banner as-is. No nudge channel (P2 bound).
        const deadlineCue = order.confirmDeadlineUtc
          ? ` Подтвердите до ${formatTs(order.confirmDeadlineUtc)} — иначе результат будет принят автоматически.`
          : '';
        banner = {
          tone: 'accent',
          title: 'Результат готов к проверке',
          text: 'Изучите фото результата и подтвердите — после этого исполнитель отправит заказ. Что-то не так — откройте спор.' + deadlineCue,
        };
        action = { kind: 'confirmResult' };
      }
      break;
    case 'Shipped': {
      // TASK-256: the ETA line follows the calendar — once its day passed,
      // promising a future delivery reads wrong; acknowledge the delay.
      // TASK-258: «сегодня» on the promised day; the closing sentence now
      // explains how the handoff ends instead of leaving a dead end.
      // G8-D3: with a tracking number the opening sentence switches by the
      // G8-D1 method snapshot — самовывоза vs courier (pure shippedTrackingView).
      // audit5-B1a (500f41b1): the customer closes the lifecycle themselves
      // via the new confirm-delivery action (block under this banner) — the
      // handoff no longer dead-ends on the provider's Completed write.
      // 31c9ace7: carrier snapshot preferred over the shape guess.
      const tracking = buildShippedTrackingView({
        trackingNumber: order.trackingNumber,
        deliveryMethod: order.deliveryMethod,
        deliveryCarrier: order.deliveryCarrier,
      });
      const opening = tracking.trackingNumber != null
        ? tracking.handoffText
        : 'Проверьте заказ при получении.';
      const eta = order.estimatedDeliveryUtc
        ? isPastCalendarDay(order.estimatedDeliveryUtc)
          ? ` Ожидаемая дата доставки (${formatEtaDay(order.estimatedDeliveryUtc)}) уже прошла — проверьте, где посылка.`
          : ` Ожидаемая доставка — ${formatEtaDay(order.estimatedDeliveryUtc)}.`
        : '';
      banner = {
        tone: 'accent',
        title: 'Заказ в пути',
        text: `${opening}${eta} Всё в порядке — подтвердите получение ниже; что-то не так — откройте спор.`,
      };
      action = { kind: 'dispute' };
      break;
    }
    case 'Completed':
      banner = {
        tone: 'success',
        title: 'Заказ завершён',
        // TASK-258: real completion stamp when the API has one — the
        // handoff ends on a dated note instead of a floating "done".
        // TASK-667a55be: a dispute resolved in the customer's favour keeps
        // the success tone but closes the dispute loop with the support note.
        // TASK-c82f2eb1 (audit-2 P4): the review CTA now renders right under
        // this banner (ReviewsBlock submit path, pre-bound to the order),
        // so the text points at it instead of the provider/product page.
        text: order.completedAtUtc
          ? `Завершён ${formatTs(order.completedAtUtc)}. Спасибо! Можно оставить отзыв о заказе ниже.`
          : 'Спасибо! Можно оставить отзыв о заказе ниже.',
      };
      if (order.disputeResolutionNote) {
        banner = {
          ...banner,
          text: `${banner.text} Решение по спору: ${order.disputeResolutionNote}`,
        };
      }
      break;
    case 'Cancelled':
      // TASK-667a55be: an order cancelled by support after a dispute must not
      // read as a silent dead end — the resolution note explains the outcome.
      banner = order.disputeResolutionNote
        ? {
            tone: 'danger',
            title: 'Заказ отменён после спора',
            text: `${order.disputeResolvedAtUtc ? `${formatTs(order.disputeResolvedAtUtc)} — ` : ''}Решение поддержки: ${order.disputeResolutionNote}`,
          }
        : {
            tone: 'danger',
            title: 'Заказ отменён',
            text: null,
          };
      break;
    case 'Disputed': {
      // TASK-667a55be: echo the customer's own reason (matrix B5 customer
      // side) — they wrote it once in the form; the detail confirms it landed.
      const when = order.disputedAtUtc ? `${formatTs(order.disputedAtUtc)} — ` : '';
      const reason = order.disputeReason?.trim() ? `Причина: ${order.disputeReason!.trim()} ` : '';
      banner = {
        tone: 'danger',
        title: 'Открыт спор',
        text: `${when}${reason}Поддержка изучит ситуацию и вернётся с решением.`,
      };
      break;
    }
  }

  return { banner, action, cues, photos: { progress, result }, resultSubmittedAtUtc, canCancel, delivery: buildDeliveryView(order) };
}
